<template>
  <!-- 小程序信息（公开消息/文章）：线性列表分页，最多三级 -->
  <div class="mini-information">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true">
        <template #default>
          <el-input v-model="searchInfo.searchKey" placeholder="标题/副标题" @keydown.enter="resetSearch" clearable>
            <template #prefix>关键字</template>
          </el-input>
          <el-tree-select v-model="searchInfo.parentId" :data="parentFilterOptions" check-strictly clearable filterable
                          :render-after-expand="false" placeholder="全部上级节点"
                          :props="{children: 'children', label: 'informationTitle'}" node-key="id">
            <template #prefix>上级节点</template>
          </el-tree-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <page-button type="write" el-type="success" @click="toAdd">+ 新建信息</page-button>
        </template>
      </search-condition>
      <list-table>
        <yq-table :data="dataList" height="100%" stripe border page-name="miniInformationList">
          <yq-table-column label="标题" prop="informationTitle" min-width="260rem">
            <template v-slot="{row}">
              <div class="title-cell">
                <image-show v-if="row.headerImage" v-model="row.headerImage" width="0.6rem" height="0.6rem"/>
                <span>{{ row.informationTitle || '—' }}</span>
              </div>
            </template>
          </yq-table-column>
          <yq-table-column label="副标题" prop="subTitle" min-width="180rem" :show-overflow-tooltip="true">
            <template v-slot="{row}">{{ row.subTitle || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="摘要" prop="informationSummary" min-width="220rem" :show-overflow-tooltip="true">
            <template v-slot="{row}">{{ row.informationSummary || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="排序" prop="informationIndex" width="90rem" align="center">
            <template v-slot="{row}">{{ row.informationIndex == null ? '—' : row.informationIndex }}</template>
          </yq-table-column>
          <yq-table-column label="层级" prop="dataLevel" width="100rem" align="center">
            <template v-slot="{row}">
              <el-tag size="small" :type="levelTagType(row.dataLevel)">{{ levelName(row.dataLevel) }}</el-tag>
            </template>
          </yq-table-column>
          <yq-table-column label="上级节点" prop="parentTitle" min-width="180rem" :show-overflow-tooltip="true">
            <template v-slot="{row}">{{ row.parentTitle || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="操作" prop="options" align="center" fixed="right" width="160rem" :show-overflow-tooltip="false">
            <template v-slot="{row}">
              <table-button @click="toEdit(row.id)">编辑</table-button>
              <table-button el-type="danger" message="是否确认删除该信息" @click="deleteRow(row.id)">删除</table-button>
            </template>
          </yq-table-column>
        </yq-table>
      </list-table>
      <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                 :total-count="searchInfo.totalCount" @onLoad="queryList"/>
    </list-page>
  </div>
</template>

<script>
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import ImageShow from "@/components/h5/image/ImageShow.vue";
import Util from "@/util/util";
import miniInformationApi from "@/api/normal/sys/miniInformationApi";
import {useRoute, useRouter} from "vue-router";

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "MiniProgramInformation",
  components: {
    ImageShow, YqTableColumn, TableButton, PageButton,
    SearchCondition, FormPage, ListTable, ListPage
  },
  data() {
    return {
      searchInfo: {pageNumber: 1, pageSize: 20, searchKey: "", parentId: ""},
      dataList: [],
      // 上级节点筛选：只给 1、2 级节点，选一级只返回它的二级数据
      parentFilterOptions: [],
    };
  },
  beforeRouteLeave(to, from, next) {
    // 将当前页面数据进行缓存
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    if (!Util.loadPageData(this)) { // 加载缓存数据失败，初始化页面
      this.loadParentOptions();
      this.resetSearch();
    }
  },
  methods: {
    async loadParentOptions() {
      const tree = await miniInformationApi.queryTree() || [];
      this.parentFilterOptions = this.filterParentNodes(JSON.parse(JSON.stringify(tree)));
    },
    // 上级节点只能是 1、2 级；3 级节点不能再有下级
    filterParentNodes(nodes) {
      return (nodes || [])
          .filter(node => (node.dataLevel || 1) < 3)
          .map(node => ({...node, children: this.filterParentNodes(node.children)}));
    },
    async queryList() {
      const {records, totalCount} = await miniInformationApi.queryList(this.searchInfo);
      this.dataList = records || [];
      this.searchInfo.totalCount = totalCount;
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.queryList();
    },
    levelName(level) {
      return {1: "一级", 2: "二级", 3: "三级"}[level] || "—";
    },
    levelTagType(level) {
      return {1: "primary", 2: "success", 3: "info"}[level] || "info";
    },
    toAdd() {
      this.router.push("/sys/mini/setting/info/edit");
    },
    toEdit(id) {
      this.router.push({path: "/sys/mini/setting/info/edit", query: {id}});
    },
    async deleteRow(id) {
      await miniInformationApi.delete(id);
      this.resetSearch();
      this.loadParentOptions();
    },
  }
};
</script>

<style lang="scss" scoped>
.mini-information {
  height: 100%;

  .title-cell {
    display: flex;
    align-items: center;
    gap: 0.12rem;
  }
}
</style>
